import axios from "axios";
import { BASE_URL } from "../../constants/config";
import { useAuth } from "../../components/context/AuthContext";
import {
  ERROR_DEFAULT_MESSAGE,
  FORMDATA,
  SUCCESS_DEFAULT_MESSAGE,
} from "../../constants/constants";
import { App } from "antd";

function useAxios() {
  const { authToken, logout } = useAuth();
  const { message } = App.useApp();

  const get = async (options: any) => {
    const {
      api,
      loader,
      successHandler,
      isExternal,
      errorHandler,
      params,
      type,
      token: customToken,
    } = options;

    const token = customToken || authToken;

    return await axios({
      method: "get",
      headers: {
        "Content-Type": type || "application/json",
        ...(!isExternal && token ? { Authorization: `Bearer ${token}` } : {}),
      },
      url: isExternal ? api : `${BASE_URL}/${api}`,
      params,
    })
      .then((resp) => {
        const { data } = resp;
        if (successHandler) successHandler(data);
        if (loader) loader(false);
      })
      .catch((err) => {
        if (err.response?.status === 401) return logout();

        const data = err?.response?.data;
        const errorMessage =
          typeof data === "string"
            ? data
            : data?.message ||
              data?.detail ||
              err?.message ||
              ERROR_DEFAULT_MESSAGE;

        message.error(errorMessage);

        if (errorHandler) errorHandler(data || errorMessage);
        if (loader) loader(false);
      });
  };

  const set = async (options: any) => {
    const {
      api,
      loader,
      successHandler,
      errorHandler,
      params,
      methodName,
      type,
      token = authToken,
      successMessage,
    } = options;

    // Detect FormData — either by explicit type flag OR by instanceof check.
    // Never spread FormData: { ...formData } produces {} because FormData
    // entries are not enumerable properties. Pass it through as-is.
    const isFormData = type === FORMDATA || params instanceof FormData;

    const data = isFormData ? params : params;

    return await axios({
      headers: {
        // For FormData: do NOT set Content-Type manually.
        // axios detects FormData and sets multipart/form-data with the
        // correct boundary automatically. Any manual value breaks the boundary.
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      method: methodName,
      url: `${BASE_URL}/${api}`,
      data,
    })
      .then((resp) => {
        const { data } = resp;
        if (successHandler) successHandler(data);
        if (loader) loader(false);

        if (successMessage === false) return;
        message.success(
          data?.message || successMessage?.message || SUCCESS_DEFAULT_MESSAGE,
          successMessage?.duration
        );
      })
      .catch((resp) => {
        let error = ERROR_DEFAULT_MESSAGE;
        const data = resp?.response?.data;

        if (resp?.response?.status === 401) {
          error = data?.detail || data?.message || "Unauthorized Access";
          logout();
        } else if (resp?.response?.status === 500) {
          error = "Internal server error occurred";
        } else if (resp?.response?.status === 402) {
          error = "Server timeout error occurred";
        } else {
          // V2 API returns structured errors: { message: "...", error_code: "..." }
          error =
            typeof data === "string"
              ? data
              : data?.message ||
                data?.detail ||
                ERROR_DEFAULT_MESSAGE;
        }

        message.error(error);

        if (errorHandler) errorHandler(data || error);
        if (loader) loader(false);
      });
  };

  return { get, set };
}

export default useAxios;